import React, { useState, useEffect, useMemo } from 'react';
import { getAdminToken } from './AdminGate';

/**
 * AdminGames — Admin interface for managing the game catalog.
 * CRUD operations for games (name, DNS, host, genre, region, maxPlayers).
 */
export default function AdminGames() {
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState(null);

  const emptyForm = {
    name: '',
    dns: '',
    host: '',
    genre: '',
    region: 'NTSC (US)',
    gameId: '',
    pcsx2Crc: '',
    maxPlayers: '',
    playable: 'YES',
    xlinkKai: 'NO',
    voiceChat: 'NO',
    crossplay: 'NO',
    netConfig: 'YES',
    dnasInfo: '',
    comment: '',
  };
  const [form, setForm] = useState(emptyForm);

  const fetchCatalog = () => {
    fetch('/api/catalog')
      .then(res => res.json())
      .then(data => { setCatalog(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(fetchCatalog, []);

  const filtered = useMemo(() => {
    if (!search) return catalog;
    const q = search.toLowerCase();
    return catalog.filter(g =>
      g.name.toLowerCase().includes(q) ||
      g.dns.toLowerCase().includes(q) ||
      g.genre.toLowerCase().includes(q) ||
      g.host.toLowerCase().includes(q) ||
      (g.gameId && g.gameId.toLowerCase().includes(q))
    );
  }, [catalog, search]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const showMessage = (msg) => {
    setMessage(msg);
    setTimeout(() => setMessage(''), 4000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    try {
      const url = editingId ? `/api/catalog/${editingId}` : '/api/catalog';
      const method = editingId ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAdminToken()}`,
        },
        body: JSON.stringify({
          ...form,
          maxPlayers: form.maxPlayers ? parseInt(form.maxPlayers) : 0,
        }),
      });

      const data = await res.json();
      if (data.success) {
        showMessage(editingId ? '✅ Game updated with full tech specs!' : '✅ Game added to catalog!');
        setForm(emptyForm);
        setEditingId(null);
        fetchCatalog();
      } else {
        showMessage(`❌ ${data.error}`);
      }
    } catch (err) {
      showMessage(`❌ ${err.message}`);
    }
    setSaving(false);
  };

  const handleEdit = (game) => {
    setEditingId(game.id);
    setForm({
      name: game.name || '',
      dns: game.dns || '',
      host: game.host || '',
      genre: game.genre || '',
      region: game.region || 'NTSC (US)',
      gameId: game.gameId || '',
      pcsx2Crc: game.pcsx2Crc || '',
      maxPlayers: game.maxPlayers ? String(game.maxPlayers) : '',
      playable: game.playable || 'YES',
      xlinkKai: game.xlinkKai || 'NO',
      voiceChat: game.voiceChat || 'NO',
      crossplay: game.crossplay || 'NO',
      netConfig: game.netConfig || 'YES',
      dnasInfo: game.dnasInfo || '',
      comment: game.comment || '',
    });
    // Scroll to form
    document.getElementById('game-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleDelete = async (id, name) => {
    if (!confirm(`Delete "${name}" from the catalog?`)) return;
    try {
      const res = await fetch(`/api/catalog/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getAdminToken()}` },
      });
      const data = await res.json();
      if (data.success) {
        showMessage(`🗑️ Removed: ${name}`);
        fetchCatalog();
      }
    } catch (err) {
      showMessage(`❌ ${err.message}`);
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const inputClass = "w-full px-3 py-2.5 rounded-lg text-sm font-mono bg-white/[0.04] border border-white/[0.08] text-white/80 placeholder-white/20 focus:outline-none focus:border-emerald-500/40 transition-colors";
  const labelClass = "block text-[10px] font-mono text-white/30 uppercase tracking-wider mb-1.5";

  return (
    <div>
      {/* Message */}
      {message && (
        <div className="mb-4 p-3 rounded-lg bg-white/[0.03] border border-white/[0.08] text-sm font-mono text-white/70">
          {message}
        </div>
      )}

      {/* Add/Edit Form */}
      <form id="game-form" onSubmit={handleSubmit} className="mb-8 p-5 rounded-lg bg-white/[0.02] border border-white/[0.06]">
        <h3 className="text-sm font-bold text-white/70 font-mono mb-5 flex items-center justify-between">
          <span>{editingId ? '✏️ Edit Game Profile' : '➕ Add Game to Catalog'}</span>
          {editingId && (
            <span className="text-[10px] text-emerald-400 font-normal">Editing: {editingId}</span>
          )}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Name */}
          <div className="md:col-span-3">
            <label className={labelClass}>Game Title *</label>
            <input name="name" value={form.name} onChange={handleChange} placeholder="SOCOM II US NAVY SEALs [NTSC]" className={inputClass} required />
          </div>

          {/* DNS */}
          <div>
            <label className={labelClass}>DNS Address *</label>
            <input name="dns" value={form.dns} onChange={handleChange} placeholder="74.91.113.233 or N/A" className={inputClass} required />
          </div>

          {/* Host */}
          <div>
            <label className={labelClass}>Server Host</label>
            <input name="host" value={form.host} onChange={handleChange} placeholder="Sony, GameSpy, EA Games..." className={inputClass} />
          </div>

          {/* Genre */}
          <div>
            <label className={labelClass}>Genre / Category</label>
            <input name="genre" value={form.genre} onChange={handleChange} placeholder="Third Person Shooters" className={inputClass} />
          </div>

          {/* Region */}
          <div>
            <label className={labelClass}>Region</label>
            <select name="region" value={form.region} onChange={handleChange} className={inputClass}>
              <option value="NTSC (US)">NTSC (US)</option>
              <option value="PAL (EU)">PAL (EU)</option>
              <option value="NTSC (JP)">NTSC (JP)</option>
              <option value="NTSC (KO)">NTSC (KO)</option>
            </select>
          </div>

          {/* Serial / Game ID */}
          <div>
            <label className={labelClass}>Game ID / Serial</label>
            <input name="gameId" value={form.gameId} onChange={handleChange} placeholder="SCUS_972.11" className={inputClass} />
          </div>

          {/* PCSX2 CRC Hash */}
          <div>
            <label className={labelClass}>PCSX2 CRC Hash</label>
            <input name="pcsx2Crc" value={form.pcsx2Crc} onChange={handleChange} placeholder="C1B141D6" className={inputClass} />
          </div>

          {/* Max Players */}
          <div>
            <label className={labelClass}>Max Players</label>
            <input name="maxPlayers" type="number" value={form.maxPlayers} onChange={handleChange} placeholder="16" className={inputClass} />
          </div>

          {/* Net Config Required */}
          <div>
            <label className={labelClass}>In-Game Net Config</label>
            <input name="netConfig" value={form.netConfig} onChange={handleChange} placeholder="YES / NO" className={inputClass} />
          </div>

          {/* Playable Online */}
          <div>
            <label className={labelClass}>Playable Online</label>
            <select name="playable" value={form.playable} onChange={handleChange} className={inputClass}>
              <option value="YES">YES</option>
              <option value="NO">NO</option>
            </select>
          </div>

          {/* XLink Kai */}
          <div>
            <label className={labelClass}>XLink Kai Tunneling</label>
            <select name="xlinkKai" value={form.xlinkKai} onChange={handleChange} className={inputClass}>
              <option value="NO">NO</option>
              <option value="YES">YES</option>
            </select>
          </div>

          {/* Voice Chat */}
          <div>
            <label className={labelClass}>Voice Chat Support</label>
            <select name="voiceChat" value={form.voiceChat} onChange={handleChange} className={inputClass}>
              <option value="NO">NO</option>
              <option value="YES">YES</option>
            </select>
          </div>

          {/* Crossplay */}
          <div>
            <label className={labelClass}>Crossplay Support</label>
            <input name="crossplay" value={form.crossplay} onChange={handleChange} placeholder="YES / NO / N/A" className={inputClass} />
          </div>

          {/* DNAS Info */}
          <div className="md:col-span-3">
            <label className={labelClass}>DNAS Patch & Setup Info</label>
            <textarea name="dnasInfo" value={form.dnasInfo} onChange={handleChange} rows={2} placeholder="Requires an Original Version + DNAS Bypass Code..." className={inputClass} />
          </div>

          {/* Comment */}
          <div className="md:col-span-3">
            <label className={labelClass}>Community & Technical Notes</label>
            <textarea name="comment" value={form.comment} onChange={handleChange} rows={2} placeholder="Host must port forward UDP 3658..." className={inputClass} />
          </div>
        </div>

        <div className="mt-5 flex items-center gap-3">
          <button
            type="submit"
            disabled={saving}
            className="px-5 py-2.5 rounded-lg bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 text-sm font-mono hover:bg-emerald-500/30 transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving...' : editingId ? 'Update Game Profile' : 'Add Game'}
          </button>
          {editingId && (
            <button type="button" onClick={handleCancel} className="px-4 py-2.5 rounded-lg text-white/30 text-sm font-mono hover:text-white/60 transition-colors">
              Cancel
            </button>
          )}
        </div>
      </form>

      {/* Game List */}
      <div>
        <div className="flex items-center justify-between gap-4 mb-4">
          <h3 className="text-xs font-mono text-white/30 uppercase tracking-wider">
            All Games ({catalog.length})
          </h3>
          {/* Search */}
          <div className="relative max-w-xs flex-1">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/20" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search catalog..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 rounded-lg text-xs font-mono bg-white/[0.04] border border-white/[0.08] text-white/70 placeholder-white/20 focus:outline-none focus:border-emerald-500/30 transition-colors"
            />
          </div>
        </div>

        {loading ? (
          <div className="text-white/20 text-xs font-mono py-4">Loading catalog...</div>
        ) : filtered.length === 0 ? (
          <div className="text-white/15 text-xs font-mono py-8 text-center">
            {search ? 'No games match your search' : 'No games in catalog'}
          </div>
        ) : (
          <div className="space-y-1 max-h-[500px] overflow-y-auto pr-1" style={{ scrollbarWidth: 'thin' }}>
            {filtered.map(game => (
              <div key={game.id} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-white/[0.02] border border-white/[0.04] hover:border-white/[0.08] transition-colors group">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white/75 font-medium truncate group-hover:text-white/90 transition-colors">
                    {game.name}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    <code className="text-[10px] font-mono text-emerald-400/60">
                      {game.dns?.toUpperCase() === 'N/A' ? 'N/A' : game.dns?.split(' or ')[0]?.split('/')[0]}
                    </code>
                    <span className="text-[10px] text-white/20">·</span>
                    <span className="text-[10px] font-mono text-white/30">{game.genre}</span>
                    <span className="text-[10px] text-white/20">·</span>
                    <span className="text-[10px] font-mono text-white/25">{game.region}</span>
                    <span className="text-[10px] text-white/20">·</span>
                    <span className="text-[10px] font-mono text-white/25">{game.host}</span>
                    {game.maxPlayers > 0 && (
                      <>
                        <span className="text-[10px] text-white/20">·</span>
                        <span className="text-[10px] font-mono text-white/25">{game.maxPlayers}P</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => handleEdit(game)} className="px-2 py-1 rounded text-[10px] font-mono text-blue-400/60 bg-blue-400/5 border border-blue-400/10 hover:text-blue-400 hover:bg-blue-400/10 transition-colors">
                    Edit
                  </button>
                  <button onClick={() => handleDelete(game.id, game.name)} className="px-2 py-1 rounded text-[10px] font-mono text-red-400/60 bg-red-400/5 border border-red-400/10 hover:text-red-400 hover:bg-red-400/10 transition-colors">
                    Del
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {search && filtered.length > 0 && (
          <p className="mt-2 text-[10px] font-mono text-white/20">
            Showing {filtered.length} of {catalog.length} games
          </p>
        )}
      </div>
    </div>
  );
}
